import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CreditCard,
  CheckCircle2,
  Users,
  Sparkles,
  Building2,
  ExternalLink,
  Loader2,
} from "lucide-react";

const PLAN_ORDER = ["cura_starter_monthly", "cura_pro_monthly", "cura_group_monthly"];
const PLAN_PERKS = {
  cura_starter_monthly: [
    "Up to 25 workers",
    "Full compliance checklist",
    "DBS + NMC monitoring",
    "In-app audit log",
  ],
  cura_pro_monthly: [
    "Up to 100 workers",
    "Everything in Starter",
    "Priority support",
    "Coming soon: branded workspace",
  ],
  cura_group_monthly: [
    "Up to 500 workers",
    "Multi-home group visibility",
    "Priority support + onboarding",
    "Custom checklist per org (coming soon)",
  ],
};

function formatGbp(pennies) {
  return `£${(pennies / 100).toFixed(0)}`;
}

export default function Billing() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // lookup_key while loading

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: plansData }, { data: subData }] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/subscription"),
      ]);
      setPlans(plansData);
      setSub(subData);
    } catch (e) {
      toast.error("Could not load billing", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Post-checkout: poll status endpoint until paid then refresh
  useEffect(() => {
    const sessionId = params.get("session_id");
    const outcome = params.get("checkout");
    if (outcome === "cancel") {
      toast.info("Checkout cancelled");
      const next = new URLSearchParams(params);
      next.delete("checkout");
      setParams(next, { replace: true });
      return;
    }
    if (outcome !== "success" || !sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts > 20) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Subscription activated");
          const next = new URLSearchParams(params);
          next.delete("checkout"); next.delete("session_id");
          setParams(next, { replace: true });
          await load();
          return;
        }
      } catch (_e) { /* ignore */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, setParams, load]);

  const startCheckout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error("Could not open billing portal", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      setBusy(null);
    }
  };

  const orderedPlans = PLAN_ORDER
    .map((k) => plans.find((p) => p.lookup_key === k))
    .filter(Boolean);

  const currentPlanId = sub?.plan;
  const seatsUsed = sub?.seats_used ?? 0;
  const seatLimit = sub?.seat_limit ?? 5;
  const usagePct = Math.min(100, Math.round((seatsUsed / Math.max(seatLimit, 1)) * 100));

  return (
    <AppLayout>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps text-slate-500">Workspace</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Billing &amp; subscription
          </h1>
          <p className="text-slate-600 mt-1.5 max-w-2xl">
            Choose a plan that fits your team. Seat limits are enforced when you invite new
            workers or reviewers.
          </p>
        </div>
        {sub?.stripe_customer_id && (
          <Button
            variant="outline"
            onClick={openPortal}
            disabled={busy === "portal"}
            data-testid="billing-portal-btn"
            className="rounded-full h-10 px-5"
          >
            {busy === "portal" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
            Manage subscription
          </Button>
        )}
      </div>

      {/* Current usage */}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="billing-current-plan">
          <div className="label-caps text-slate-500">Current plan</div>
          <div className="mt-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-teal-700" />
            <div className="font-heading text-xl font-bold capitalize">{sub?.plan || "trial"}</div>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Status: <span className="font-semibold">{sub?.subscription_status || "trialing"}</span>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="label-caps text-slate-500">Seat usage</div>
          <div className="mt-2 flex items-baseline gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            <div className="font-heading text-xl font-bold" data-testid="billing-seats-used">{seatsUsed}</div>
            <div className="text-slate-500">/ {seatLimit}</div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${usagePct >= 100 ? "bg-rose-500" : usagePct > 80 ? "bg-amber-500" : "bg-teal-600"}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="label-caps text-slate-500">Renews</div>
          <div className="mt-2 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-500" />
            <div className="font-heading text-xl font-bold">
              {sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "—"}
            </div>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Cancel or change plan any time via Manage subscription.
          </div>
        </div>
      </div>

      {/* Plan cards */}
      <div className="mt-10 grid gap-5 md:grid-cols-3" data-testid="billing-plans">
        {loading && (
          <div className="col-span-3 text-slate-500 py-10 text-center">Loading plans…</div>
        )}
        {!loading && orderedPlans.map((p) => {
          const isCurrent = p.plan_id === currentPlanId;
          const perks = PLAN_PERKS[p.lookup_key] || [];
          return (
            <div
              key={p.lookup_key}
              data-testid={`plan-card-${p.plan_id}`}
              className={`rounded-2xl border-2 bg-white p-6 flex flex-col ${
                isCurrent ? "border-teal-700 ring-2 ring-teal-100" : "border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-heading text-xl font-bold">{p.display}</div>
                {isCurrent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100 px-2.5 py-0.5 text-xs font-semibold">
                    <CheckCircle2 className="h-3 w-3" /> Current
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="font-heading text-4xl font-bold">{formatGbp(p.amount)}</div>
                <div className="text-slate-500">/ {p.interval}</div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Up to {p.seat_limit} seats</div>
              <ul className="mt-5 space-y-2 flex-1">
                {perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    {perk}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startCheckout(p.lookup_key)}
                disabled={busy === p.lookup_key || isCurrent}
                data-testid={`plan-checkout-${p.plan_id}`}
                className={`mt-6 rounded-full h-11 ${
                  isCurrent
                    ? "bg-slate-100 text-slate-500"
                    : "bg-teal-700 hover:bg-teal-800 text-white"
                }`}
              >
                {isCurrent ? (
                  "Current plan"
                ) : busy === p.lookup_key ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Redirecting…</>
                ) : (
                  <><CreditCard className="h-4 w-4 mr-1.5" /> Choose {p.display}</>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-xs text-slate-500">
        Payments are securely processed by Stripe. Test mode is currently active — use card
        <span className="font-mono mx-1">4242 4242 4242 4242</span> with any future expiry
        and any CVC to try a plan.
      </p>
    </AppLayout>
  );
}
